import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { styled } from '@/components/content/stitches';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import {
  CONNECTION_RETRY_ACTION,
  CONNECTION_RETRY_COPY,
  CONNECTION_RETRY_LABEL,
  CONNECTION_RETRY_TITLE,
} from '@/utils/errorReporter';

/**
 * Shared "can't connect" state for network-layer request failures (the SDK's
 * retry layer exhausted and rethrew a fetch error with no readable response —
 * `TypeError: Failed to fetch` / `Load failed`, DNS/TLS/timeout/CORS-stripped).
 *
 * One component so every surface that hits this failure class shows the same
 * branded message + retry affordance instead of a bespoke banner, a blank
 * panel, a silent empty state, or a toast-only signal (TEAM-14780). Gate the
 * default copy on `isTransportFailure` from `@/utils/networkErrors`, not any
 * error — it also matches the `UpstreamRequestError` that `withJsonGuard`
 * wraps a network error in, which `isNetworkError` alone misses. A real
 * status-code + body response must never get the "check your connection"
 * wording: a surface that wants this panel's layout, `role="alert"` and
 * guarded "Try again" button for such a failure passes its own `heading` and
 * `message` overrides (see `features/store/menu-load-error.tsx`, TEAM-15314).
 *
 * The copy below is composed from the shared constants rather than redeclared, per the docblock on
 * `CONNECTION_RETRY_TITLE` in `utils/errorReporter.ts`: every surface showing this
 * failure class derives its wording from those two sentences so it can only be changed
 * in one place. They had already drifted — this component shipped the heading without
 * the trailing period that TEAM-14320's AC1 pins verbatim, so the account panels and the
 * auth banners rendered different copy for the same failure (TEAM-14780 AC4).
 *
 * It is also the ONLY connection-error component: the auth forms' former
 * `uielements/connectionErrorBanner` was folded in (TEAM-14780 QA, 2026-09-28), bringing
 * its duplicate-press guard with it, so every surface shares one retry button — the brand
 * primary button — instead of a red one on sign-up and a navy one on the account pages.
 */
export const CONNECTION_ERROR_HEADING = CONNECTION_RETRY_TITLE;
export const CONNECTION_ERROR_BODY = CONNECTION_RETRY_ACTION;
/** Single-string form for the inline banner variant; matches the two-line copy. */
export const CONNECTION_ERROR_BANNER_COPY = CONNECTION_RETRY_COPY;

/**
 * How long the synchronous duplicate-press guard stays armed when nothing else clears it.
 * It only has to outlive the render-commit gap TEAM-14915 was closing (a few milliseconds);
 * a second is generous for that while still letting a diner who waits a beat press again.
 */
const RETRY_REARM_FALLBACK_MS = 1000;

interface Props {
  /** When provided, renders a "Try again" button wired to this handler. */
  onRetry?: () => void;
  /**
   * `panel` (default) — a centered page-level card for account/menu pages.
   * `banner` — a compact inline strip for use above a form (e.g. sign-up).
   */
  variant?: 'panel' | 'banner';
  /** Panel heading override. Ignored by the banner variant. */
  heading?: ReactNode;
  /** Body copy override. Defaults to the shared copy for the active variant. */
  message?: ReactNode;
  retryLabel?: string;
  retryingLabel?: string;
  /** Disables the retry button and swaps in `retryingLabel` while a retry runs. */
  isRetrying?: boolean;
  'data-testid'?: string;
  retryTestId?: string;
}

export function ConnectionErrorState({
  onRetry,
  variant = 'panel',
  heading = CONNECTION_ERROR_HEADING,
  message,
  retryLabel = CONNECTION_RETRY_LABEL,
  retryingLabel = 'Trying again…',
  isRetrying = false,
  'data-testid': dataTestId,
  retryTestId,
}: Props) {
  /**
   * TEAM-14915: `disabled={isRetrying}` only takes hold on the next React commit, so between a
   * press and the consumer reporting `isRetrying` the button is still live. An impatient diner
   * double/triple-tapping "Try again" in that window fires one request per tap — duplicate
   * login attempts, or several password-reset emails, for a single intended retry. This ref
   * drops any press while a retry is already in flight, synchronously, so exactly one request
   * leaves per settle regardless of render timing.
   */
  const retryInFlight = useRef(false);
  const rearmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Touches refs only, so it is stable for the life of the component.
  const rearm = useCallback(() => {
    retryInFlight.current = false;
    if (rearmTimer.current) {
      clearTimeout(rearmTimer.current);
      rearmTimer.current = null;
    }
  }, []);

  // Preferred re-arm: the consumer reports the cycle finished (`isRetrying` returns to false
  // because the retry failed again and the state is still shown; on success it unmounts).
  useEffect(() => {
    if (!isRetrying) {
      rearm();
    }
  }, [isRetrying, rearm]);

  // Clear a pending re-arm timer on unmount.
  useEffect(() => rearm, [rearm]);

  const handleRetry = () => {
    if (!onRetry || isRetrying || retryInFlight.current) {
      return;
    }
    retryInFlight.current = true;
    /**
     * TEAM-14780: the effect above only runs when `isRetrying` actually changes. When the
     * retried request fails faster than React can commit an `isRetrying === true` render —
     * offline mode, a blocked or unresolvable host, an ad-blocker, a CORS rejection — or when
     * a consumer never reports it at all, the ref would stay latched and every later press be
     * silently dropped while the button still reads "Try again". This fallback guarantees the
     * guard can never outlive the press that set it.
     */
    rearmTimer.current = setTimeout(rearm, RETRY_REARM_FALLBACK_MS);
    onRetry();
  };

  const retryButton = onRetry ? (
    <StyledPrimaryButton
      data-css-override="StyledPrimaryButton"
      type="button"
      data-testid={retryTestId}
      size={variant === 'banner' ? 'small' : undefined}
      disabled={isRetrying}
      onClick={handleRetry}
    >
      {isRetrying ? retryingLabel : retryLabel}
    </StyledPrimaryButton>
  ) : null;

  if (variant === 'banner') {
    return (
      <Banner role="alert" data-testid={dataTestId}>
        <BannerMessage>{message ?? CONNECTION_ERROR_BANNER_COPY}</BannerMessage>
        {retryButton}
      </Banner>
    );
  }

  return (
    <Panel role="alert" data-testid={dataTestId}>
      <Heading>{heading}</Heading>
      <Paragraph>{message ?? CONNECTION_ERROR_BODY}</Paragraph>
      {retryButton}
    </Panel>
  );
}

const Panel = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '$4',
  padding: '$14',
  textAlign: 'center',
});

const Heading = styled('h2', {
  fontSize: '$text400',
  fontWeight: 'bold',
  textAlign: 'center',
  margin: 0,
});

const Paragraph = styled('p', {
  fontSize: '$text200',
  textAlign: 'center',
  margin: 0,
});

const Banner = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px 16px',
  marginBottom: '16px',
  // Colours route through `--connection-error__*` custom properties (carried over from the
  // former auth-form banner) so a brand can retheme it; the hexes are fallbacks only.
  border: '1px solid var(--connection-error__border-color, #d93025)',
  borderRadius: '4px',
  backgroundColor: 'var(--connection-error__background-color, #fdecea)',
  color: 'var(--connection-error__color, #5f1414)',
  '@sm': {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const BannerMessage = styled('p', {
  margin: '0 !important',
  fontSize: '14px',
  lineHeight: '20px',
  whiteSpace: 'pre-line',
  paddingRight: '12px',
  paddingLeft: '12px',
});
